import React, { useCallback, useContext, useEffect, useState } from 'react';
import { style } from 'typestyle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShieldAccountOutlineIcon from 'mdi-react/ShieldAccountOutlineIcon';
import StarIcon from 'mdi-react/StarIcon';
import StarOutlineIcon from 'mdi-react/StarOutlineIcon';
import { useTranslation } from 'react-i18next';
import PermittedAdmin from '../models/permitted-admin';
import useAdmins from '../hooks/admin.hook';
import { createAdmin, deleteAdmin, setOfficeOwner } from '../services/admins.services';
import ActivityIndicator from '../../../core/components/activity-indicator/activity-indicator.component';
import { Center } from '../../../core/components/center/center.component';
import notify from '../../../core/components/notify/notify.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import StyleConstants from '../../../core/styles.constants';
import stringIsNullOrWhitespace from '../../../core/utils/validation-utils';
import { isValidEmail } from '../../../core/utils/email-utils';
import * as accessStyles from '../office-access.styles';
import * as workspaceStyles from '../office-workspace.styles';
import * as surfaceStyles from '../../../core/admin-surface.styles';
import * as tableStyles from '../office-table.styles';
import * as styles from '../offices.styles';
import ConfirmationModal from '../modals/confirmation-modal.component';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import AppConstants from '../../../core/app.constants';

interface Props {
  readonly officeId: string | undefined;
}

// Administrators are stored by e-mail only, so the avatar falls back to the first two characters
// of the address instead of initials.
const avatarLabel = (email: string): string => email.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase();

const OfficeAdminGroup = ({ officeId }: Props): React.JSX.Element => {
  const keycloak = useKeycloak();
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');

  const [addError, setAddError] = useState<string | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(true);
  const [adminToDelete, setAdminToDelete] = useState<PermittedAdmin | undefined>(undefined);
  const [adminToMakeOwner, setAdminToMakeOwner] = useState<PermittedAdmin | undefined>(undefined);
  const [isTransferring, setIsTransferring] = useState(false);

  const [adminsToShow, setAdminsToShow] = useState<PermittedAdmin[] | undefined>(undefined);
  const { admins } = useAdmins({ id: officeId });

  useEffect(() => {
    if (admins != null && adminsToShow == null) {
      if (isLoading) {
        setIsLoading(false);
      }

      const owner = admins.filter((permittedAdmin) => permittedAdmin.isAuthor);
      const otherAdmins = admins.filter((permittedAdmin) => !permittedAdmin.isAuthor);

      setAdminsToShow([...owner, ...otherAdmins]);
    }
  }, [admins, isLoading, adminsToShow]);

  // Whyellow staff only, and the same rule the server enforces. Every other action on this card is
  // open to an office administrator; ownership is not, because an administrator who can move it can
  // take it. Hidden rather than disabled: a button an administrator can never use is not a feature
  // they are missing, it is noise on their screen.
  const canTransferOwnership = keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE);

  const handleTransferOwnership = useCallback(
    async (newOwner: PermittedAdmin): Promise<void> => {
      if (!officeId) return;
      if (adminsToShow == null) return;

      setIsTransferring(true);
      const outcome = await setOfficeOwner(officeId, newOwner.email, keycloak);
      setIsTransferring(false);

      if (outcome.error != null) {
        // The server explains every refusal on this endpoint — "add them as an administrator
        // first", "they already own it" — so its sentence is shown rather than a status code.
        notify(outcome.error, false);
        return;
      }

      // Rebuilt from the address the server stored rather than the one that was clicked: the match
      // is case-insensitive, so the two can differ, and the owner chip is driven by this flag.
      const ownerEmail = outcome.owner?.ownerEmail ?? newOwner.email;

      const updated = adminsToShow.map((admin) => {
        const next = new PermittedAdmin();
        next.id = admin.id;
        next.email = admin.email;
        next.isAuthor = admin.email.toLowerCase() === ownerEmail.toLowerCase();
        return next;
      });

      // The owner stays pinned to the top of the list, as it is on load.
      setAdminsToShow([...updated.filter((admin) => admin.isAuthor), ...updated.filter((admin) => !admin.isAuthor)]);
      notify(t('office-owner-changed-success-message'), true);
    },
    [officeId, adminsToShow, keycloak, t],
  );

  const handleDeleteAdmin = useCallback(
    async (permittedAdmin: PermittedAdmin): Promise<void> => {
      if (!officeId) return;
      if (adminsToShow == null) return;

      const result = await deleteAdmin(officeId, permittedAdmin.email, keycloak);

      if (result) {
        setAdminsToShow(adminsToShow.filter((admin) => admin.email !== permittedAdmin.email));
        notify(t('permitted-admin-deleted-success-message'), true);
      }
    },
    [officeId, adminsToShow, keycloak, t],
  );

  // Returns the reason the address can't be added, or undefined when it is fine.
  const addAdminProblem = useCallback(
    (candidate: string): string | undefined => {
      if (stringIsNullOrWhitespace(candidate)) {
        return t('email-empty-message') ?? '';
      }

      if (!isValidEmail(candidate)) {
        return t('invalid-email-message') ?? '';
      }

      if ((adminsToShow ?? []).some((admin) => admin.email.toLowerCase() === candidate.trim().toLowerCase())) {
        return t('email-already-added-message') ?? '';
      }

      return undefined;
    },
    [adminsToShow, t],
  );

  const handleAddAdmin = useCallback(async (): Promise<void> => {
    if (!officeId) return;
    if (adminsToShow == null) return;

    const problem = addAdminProblem(email);
    if (problem != null) {
      setAddError(problem);
      notify(problem, false);
      return;
    }

    const trimmedEmail = email.trim();
    const result = await createAdmin(officeId, trimmedEmail, keycloak);

    if (result) {
      const newAdmin = new PermittedAdmin();
      newAdmin.email = trimmedEmail;

      // The office owner stays pinned to the top; everyone else is sorted by address. Destructuring
      // keeps this safe when the list is somehow empty — pushing `adminsToShow[0]` blindly would
      // put `undefined` in state and break the render.
      const [owner, ...otherAdmins] = adminsToShow;
      const sortedOtherAdmins = [...otherAdmins, newAdmin].sort((a, b) => (a.email.toLowerCase() > b.email.toLowerCase() ? 1 : -1));

      setAdminsToShow(owner != null ? [owner, ...sortedOtherAdmins] : sortedOtherAdmins);
      setEmail('');
      setAddError(undefined);
      notify(t('permitted-admin-added-success-message'), true);
    }
  }, [officeId, adminsToShow, email, addAdminProblem, keycloak, t]);

  const renderList = (): React.JSX.Element => {
    if (isLoading) {
      return (
        <div className={style(accessStyles.loaderPad)}>
          <Center>
            <ActivityIndicator size={20} color={StyleConstants.GREY_COLOR} />
          </Center>
        </div>
      );
    }

    if (adminsToShow === undefined || adminsToShow.length === 0) {
      return (
        <div className={style(accessStyles.cardPadded)}>
          <span className={style(styles.emptyListMessage)}>{t('no-office-admins-message')}</span>
        </div>
      );
    }

    return (
      <ul className={style(accessStyles.adminList)}>
        {adminsToShow.map((admin, index) => (
          <li className={style(accessStyles.adminRow)} key={admin.email}>
            <div className={style(workspaceStyles.avatar(workspaceStyles.accentAt(index)))}>{avatarLabel(admin.email)}</div>
            <div className={style(accessStyles.adminWho)}>
              <span className={style(accessStyles.adminEmail)}>{admin.email}</span>
              {admin.isAuthor && (
                <span className={style(accessStyles.ownerChip)}>
                  <StarIcon size={12} />
                  {t('office-owner')}
                </span>
              )}
            </div>
            {!admin.isAuthor && (
              <div className={style(accessStyles.adminActions)}>
                {canTransferOwnership && (
                  <button
                    type="button"
                    className={style(tableStyles.rowActionButton)}
                    aria-label={`${t('make-office-owner')} ${admin.email}`}
                    title={t('make-office-owner').toString()}
                    disabled={isTransferring}
                    onClick={(): void => setAdminToMakeOwner(admin)}
                  >
                    <StarOutlineIcon size={17} />
                  </button>
                )}
                <button
                  type="button"
                  className={style(tableStyles.rowActionButton)}
                  aria-label={`${t('delete')} ${admin.email}`}
                  title={t('remove-admin-rights').toString()}
                  onClick={(): void => setAdminToDelete(admin)}
                >
                  <ShieldAccountOutlineIcon size={17} />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  const isSoleAdmin = !isLoading && adminsToShow != null && adminsToShow.length === 1;

  return (
    <div className={style(surfaceStyles.card)}>
      <div className={style(surfaceStyles.cardHead(isMobile))}>
        <div>
          <h2 className={style(surfaceStyles.cardTitle)}>{t('office-administrators')}</h2>
          <p className={style(surfaceStyles.cardSub)}>{t('office-administrators-card-subtitle')}</p>
        </div>
        <InfoOutlinedIcon
          data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
          data-tooltip-content={t('add-admin-info-message').toString()}
          sx={styles.tooltipIconSX}
        />
      </div>

      <div className={style(accessStyles.adminAddRow(isMobile))}>
        <input
          type="email"
          maxLength={100}
          className={style(surfaceStyles.textInput)}
          placeholder={t('add-admin').toString()}
          aria-label={t('add-admin').toString()}
          aria-invalid={addError != null}
          value={email}
          onChange={(event): void => {
            setEmail(event.target.value);
            setAddError(undefined);
          }}
          onKeyDown={(event): void => {
            if (event.key === 'Enter') {
              handleAddAdmin();
            }
          }}
        />
        <button
          type="button"
          className={style(surfaceStyles.primaryButton)}
          onClick={handleAddAdmin}
          disabled={adminsToShow == null || stringIsNullOrWhitespace(email)}
        >
          {t('add')}
        </button>
      </div>
      {addError != null && (
        <span className={`${style(surfaceStyles.fieldError)} ${style(accessStyles.adminAddError)}`}>{addError}</span>
      )}

      {renderList()}

      {isSoleAdmin && (
        <div className={style(accessStyles.cardPadded)}>
          <div className={style(workspaceStyles.alert)}>
            <span className={style(workspaceStyles.alertIcon)}>
              <ShieldAccountOutlineIcon size={18} />
            </span>
            <span>{t('sole-admin-message')}</span>
          </div>
        </div>
      )}

      <ConfirmationModal
        showModal={adminToDelete != null}
        itemName={adminToDelete?.email ?? ''}
        handleAccept={(): void => {
          if (adminToDelete != null) {
            handleDeleteAdmin(adminToDelete);
          }
          setAdminToDelete(undefined);
        }}
        handleClose={(): void => setAdminToDelete(undefined)}
      />

      <ConfirmationModal
        showModal={adminToMakeOwner != null}
        isLoading={isTransferring}
        itemName={adminToMakeOwner?.email ?? ''}
        messageKey="about-to-make-office-owner"
        confirmLabelKey="make-office-owner"
        handleAccept={(): void => {
          if (adminToMakeOwner != null) {
            handleTransferOwnership(adminToMakeOwner).catch(() => undefined);
          }
          setAdminToMakeOwner(undefined);
        }}
        handleClose={(): void => setAdminToMakeOwner(undefined)}
      />
    </div>
  );
};

export default OfficeAdminGroup;
